import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthApi } from '@core/auth/services/auth-api';
import { Footer } from "@shared/components/footer/footer";
import { FormUtils } from '@shared/utils/form.utils';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { DataEndpoint } from '@core/models/general.model';
import { Router } from '@angular/router';
import { Routes } from '@core/routes/routes';
import { MessageService } from 'primeng/api';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { Password } from "primeng/password";
import { AuthLogin, ConfirmForgotPasswordRequest } from '@core/auth/models/auth.model';
import { ToastModule } from 'primeng/toast';
import { AuthService } from '@core/auth/services/auth-service';
import { AppDialog } from "@shared/components/dialog";

@Component({
  selector: 'app-recover-password',
  imports: [
    CommonModule,
    Footer,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    MessageModule,
    ButtonModule,
    Password,
    ToastModule,
    AppDialog
],
  templateUrl: './recover-password.html',
  styleUrl: './recover-password.scss',
  providers: [MessageService]
})
export class RecoverPassword {
  private authApi = inject(AuthApi);
  private authService = inject(AuthService);
  private messageService = inject(MessageService);
  private router = inject(Router);

  steps: number = 3
  step: number = 1

  form: FormGroup = new FormGroup({
    email: new FormControl(null, [
      Validators.required,
      Validators.email
    ]),
    verificationCode: new FormControl(null, [
      Validators.required
    ]),
    password: new FormControl(null, [
      Validators.required,
      FormUtils.hasUpperCaseValidator,
      FormUtils.hasLowerCaseValidator,
      FormUtils.hasNumberValidator,
      FormUtils.hasSymbolValidator,
      Validators.minLength(8)
    ]),
    confirmPassword: new FormControl(null, [
      Validators.required
    ])
  });

  isFormSubmitted = false;
  isLoading = false;

  visibleEmailSentDialog: boolean = false;
  visibleFinishedDialog: boolean = false;

  onSubmitEmail() {
    this.isFormSubmitted = true;
    if (this.form.get('email')?.valid) {
      this.isLoading = true;

      this.authApi.postForgotPassword(this.form.get('email')?.value).subscribe({
        next: (response: DataEndpoint<any>) => {
          if (response.Status === 200) {
            this.isFormSubmitted = false;
            this.visibleEmailSentDialog = true;
            this.step = 2;
          }
          this.isLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error?.error.Message
          });
        }
      })
    }
  }

  onSubmitVerificationCode() {
    this.isFormSubmitted = true;
    if (this.form.get('verificationCode')?.valid) {
      this.isLoading = true;

      setTimeout(() => {
        this.isFormSubmitted = false;
        this.isLoading = false;
        this.step = 3;
      }, 1000);
    }
  }

  onSubmitConfirmPassword() {
    this.isFormSubmitted = true;

    if (
      this.form.get('password')?.valid && 
      this.form.get('confirmPassword')?.valid && 
      this.isValidConfirmPassword()
    ) {
      this.isLoading = true;

      const request: ConfirmForgotPasswordRequest = {
        UserName: this.form.get('email')!.value,
        Code: this.form.get('verificationCode')!.value,
        NewPassword: this.form.get('password')!.value
      }

      this.authApi.postConfirmForgotPassword(request).subscribe({
        next: (response: DataEndpoint<any>) => {
          if (response.Status === 200) {
            this.isFormSubmitted = false;
            this.visibleFinishedDialog = true;
          }
          this.isLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error?.error.Message
          });
        }
      })
    }
  }

  login() {
    this.isFormSubmitted = true;
    
    if (this.form.invalid) {
      return;
    }

    this.isLoading = true;

    const credentials: AuthLogin = {
      UserName: this.form.get('email')!.value,
      Password: this.form.get('password')!.value
    };

    this.authService.login(credentials).subscribe({
      next: (_) => {
        this.form.reset();
        this.isFormSubmitted = false;
        this.isLoading = false;
      },
      error: (_) => {
        this.isLoading = false;
        this.router.navigate([Routes.loginPage]);
      }
    });
  }
  
  isInvalid(controlName: string) {
    const control = this.form.get(controlName);
    return control?.invalid && (control.touched || this.isFormSubmitted);
  }

  getErrorMessage(controlName: string): string {
    const control = this.form.get(controlName);
    return FormUtils.getErrorMessage(control, this.isFormSubmitted);
  }

  getConfirmPasswordErrorMessage(controlName: string): string {
    const control = this.form.get(controlName);
  
    // Verificar que el campo no esté vacío
    const baseError = FormUtils.getErrorMessage(control, this.isFormSubmitted);
    if (baseError) {
      return baseError;
    }

    // Si no está vacío, verificar match con password
    if (!this.isValidConfirmPassword()) {
      return 'Las contraseñas ingresadas no coinciden.';
    }
    
    return '';
  }

  isValidConfirmPassword() {
    const confirmPassword = this.form.get('confirmPassword');
    if (confirmPassword?.touched || this.isFormSubmitted) {
      const password = this.form.get('password');
      
      if (password && confirmPassword && password?.value !== confirmPassword?.value) {
        return false;
      }
      return true
    }
    return true
  }

  onCloseEmailSentDialog() {
    this.visibleEmailSentDialog = false;
  }

  onCloseFinishedDialog() {
    this.visibleFinishedDialog = false;
    this.login();
  }

  redirectToLogin() {
    this.router.navigate([Routes.loginPage]);
  }
}
